import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ConfirmDialog, Modal, PromptDialog } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  GripVertical,
  Plus,
  Trash2,
  Copy,
  FolderOpen,
  Bookmark,
  Clock,
  Search,
  Grid,
  List,
  LayoutGrid,
  Download,
  Upload,
  Layers,
  RefreshCw,
  ExternalLink,
  Loader2,
  Star,
} from 'lucide-react';
import { api } from '@/services/client';
import { tabService, type SavedVisualLayout } from '@/services/tabs';
import type { ApiBrowserTab } from '@/types';
import { toast } from 'sonner';
import { openBrowserTabs } from '@/services/extensionBridge';

const WS_SESSION_KEY = 'khiimori_workspace_session_id';
const DRAFT_STORAGE_KEY = 'khiimori-drag-drop-draft';

type SourceType = 'tabs' | 'bookmarks' | 'sessions';
type ViewMode = 'grid' | 'list' | 'compact';

interface DraggableItem {
  id: string;
  name: string;
  type: 'tab' | 'session' | 'bookmark' | 'folder';
  color?: string;
  children?: DraggableItem[];
  metadata?: {
    url?: string;
    favicon?: string;
    tabCount?: number;
    folderId?: string;
    isActive?: boolean;
  };
}

interface DragDropDraft {
  tabs?: string[];
  bookmarks?: string[];
  sessions?: string[];
  viewMode?: ViewMode;
  activeSource?: SourceType;
}

interface BookmarkRow {
  id: string;
  title: string;
  url: string;
  tab_id?: string;
  folder_id?: string;
  favicon_url?: string;
}

function indexBookmarkedTabs(bookmarks: BookmarkRow[]) {
  const tabIds = new Set<string>();
  const urls = new Set<string>();
  for (const bm of bookmarks) {
    if (bm.tab_id) tabIds.add(bm.tab_id);
    if (bm.url) urls.add(bm.url);
  }
  return { tabIds, urls };
}

interface BookmarkFolderRow {
  id: string;
  name: string;
  color?: string;
  icon?: string;
}

interface SessionRow {
  id: string;
  name: string;
  color?: string;
  is_active?: boolean;
  tab_count?: number;
}

const PALETTE = ['#8b5cf6', '#10b981', '#f59e0b', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];

function colorFromString(value: string): string {
  const hash = [...value].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length];
}

function applyOrder(items: DraggableItem[], order?: string[]): DraggableItem[] {
  if (!order?.length) return items;
  const byId = new Map(items.map((item) => [item.id, item]));
  const ordered = order.map((id) => byId.get(id)).filter((item): item is DraggableItem => Boolean(item));
  const seen = new Set(ordered.map((item) => item.id));
  const rest = items.filter((item) => !seen.has(item.id));
  return [...ordered, ...rest];
}

function mapTab(tab: ApiBrowserTab): DraggableItem {
  const url = tab.url || '';
  let host = 'tab';
  try {
    host = new URL(url).hostname.replace(/^www\./, '');
  } catch {
    // ignore
  }
  return {
    id: tab.id,
    name: tab.title || host || '(untitled)',
    type: 'tab',
    color: colorFromString(host),
    metadata: { url, favicon: tab.favicon },
  };
}

function mapSession(row: SessionRow): DraggableItem {
  return {
    id: row.id,
    name: row.name || 'Untitled session',
    type: 'session',
    color: row.color || colorFromString(row.id),
    metadata: {
      tabCount: row.tab_count,
      isActive: row.is_active,
    },
  };
}

function buildBookmarkItems(bookmarks: BookmarkRow[], folders: BookmarkFolderRow[]): DraggableItem[] {
  const folderMap = new Map(folders.map((f) => [f.id, f]));
  const byFolder = new Map<string, BookmarkRow[]>();
  const loose: BookmarkRow[] = [];

  for (const bm of bookmarks) {
    if (bm.folder_id && folderMap.has(bm.folder_id)) {
      const list = byFolder.get(bm.folder_id) ?? [];
      list.push(bm);
      byFolder.set(bm.folder_id, list);
    } else {
      loose.push(bm);
    }
  }

  const folderItems: DraggableItem[] = folders.map((folder) => ({
    id: `folder-${folder.id}`,
    name: folder.name,
    type: 'folder',
    color: folder.color || colorFromString(folder.name),
    metadata: { folderId: folder.id },
    children: (byFolder.get(folder.id) ?? []).map((bm) => ({
      id: bm.id,
      name: bm.title || bm.url,
      type: 'bookmark' as const,
      color: colorFromString(bm.url),
      metadata: { url: bm.url, favicon: bm.favicon_url, folderId: folder.id },
    })),
  }));

  const looseItems: DraggableItem[] = loose.map((bm) => ({
    id: bm.id,
    name: bm.title || bm.url,
    type: 'bookmark',
    color: colorFromString(bm.url),
    metadata: { url: bm.url, favicon: bm.favicon_url },
  }));

  return [...folderItems, ...looseItems];
}

function readDraft(): DragDropDraft {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as DragDropDraft) : {};
  } catch {
    return {};
  }
}

function writeDraft(draft: DragDropDraft) {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // ignore quota
  }
}

function layoutPayload(
  source: SourceType,
  viewMode: ViewMode,
  items: DraggableItem[],
  sessionId: string | null,
): Record<string, unknown> {
  return {
    page: 'drag-drop',
    source,
    session_id: sessionId,
    view_mode: viewMode,
    item_order: items.map((item) => item.id),
    items: items.map((item) => ({
      id: item.id,
      name: item.name,
      type: item.type,
      color: item.color,
      metadata: item.metadata,
      children: item.children,
    })),
    saved_at: new Date().toISOString(),
  };
}

export const DragDropPage: React.FC = () => {
  const [tabs, setTabs] = useState<DraggableItem[]>([]);
  const [bookmarks, setBookmarks] = useState<DraggableItem[]>([]);
  const [sessions, setSessions] = useState<DraggableItem[]>([]);
  const [activeSource, setActiveSource] = useState<SourceType>(() => readDraft().activeSource ?? 'tabs');
  const [viewMode, setViewMode] = useState<ViewMode>(() => readDraft().viewMode ?? 'grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [savedLayouts, setSavedLayouts] = useState<SavedVisualLayout[]>([]);
  const [bookmarkedTabIds, setBookmarkedTabIds] = useState<Set<string>>(new Set());
  const [bookmarkedUrls, setBookmarkedUrls] = useState<Set<string>>(new Set());
  const [bookmarkingId, setBookmarkingId] = useState<string | null>(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSaveLayout, setShowSaveLayout] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', url: '' });
  const [saving, setSaving] = useState(false);

  const importRef = useRef<HTMLInputElement>(null);

  const currentItems = useMemo(() => {
    switch (activeSource) {
      case 'tabs':
        return tabs;
      case 'bookmarks':
        return bookmarks;
      case 'sessions':
        return sessions;
      default:
        return [];
    }
  }, [activeSource, tabs, bookmarks, sessions]);

  const setCurrentItems = useCallback(
    (items: DraggableItem[]) => {
      setDirty(true);
      switch (activeSource) {
        case 'tabs':
          setTabs(items);
          break;
        case 'bookmarks':
          setBookmarks(items);
          break;
        case 'sessions':
          setSessions(items);
          break;
      }
    },
    [activeSource],
  );

  const refreshLayouts = useCallback(async () => {
    try {
      const list = await tabService.listVisualLayouts();
      setSavedLayouts(list.filter((l) => (l.layout as Record<string, unknown>)?.page === 'drag-drop'));
    } catch {
      setSavedLayouts([]);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    const draft = readDraft();
    try {
      let sid = sessionStorage.getItem(WS_SESSION_KEY);
      const sessionsRes = await api.get<{ sessions: SessionRow[] }>('/sessions');
      const sessionRows = sessionsRes.data.sessions ?? [];
      if (!sid) {
        const active = sessionRows.find((s) => s.is_active) ?? sessionRows[0];
        sid = active?.id ?? null;
        if (sid) sessionStorage.setItem(WS_SESSION_KEY, sid);
      }
      setSessionId(sid);

      const [tabsResult, bookmarksRes, foldersRes] = await Promise.all([
        sid ? tabService.getTabsBySession(sid, 1, 200) : Promise.resolve({ tabs: [] as ApiBrowserTab[], total: 0 }),
        api.get<{ bookmarks: BookmarkRow[] }>('/bookmarks'),
        api.get<{ folders: BookmarkFolderRow[] }>('/bookmark-folders'),
      ]);

      const bookmarkRows = bookmarksRes.data.bookmarks ?? [];
      const { tabIds, urls } = indexBookmarkedTabs(bookmarkRows);

      const mappedTabs = applyOrder(tabsResult.tabs.map(mapTab), draft.tabs);
      const mappedBookmarks = applyOrder(
        buildBookmarkItems(bookmarkRows, foldersRes.data.folders ?? []),
        draft.bookmarks,
      );
      const mappedSessions = applyOrder(sessionRows.map(mapSession), draft.sessions);

      setTabs(mappedTabs);
      setBookmarks(mappedBookmarks);
      setSessions(mappedSessions);
      setBookmarkedTabIds(tabIds);
      setBookmarkedUrls(urls);
      await refreshLayouts();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load workspace items');
    } finally {
      setLoading(false);
    }
  }, [refreshLayouts]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return currentItems;
    return currentItems.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.metadata?.url?.toLowerCase().includes(q) ||
        item.children?.some((child) => child.name.toLowerCase().includes(q)),
    );
  }, [currentItems, searchQuery]);

  const canReorder = !searchQuery.trim();

  const getItemIcon = (item: DraggableItem) => {
    switch (item.type) {
      case 'tab':
        return <Layers size={16} />;
      case 'session':
        return <Clock size={16} />;
      case 'bookmark':
        return <Bookmark size={16} />;
      case 'folder':
        return <FolderOpen size={16} />;
      default:
        return <Layers size={16} />;
    }
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) =>
      prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId],
    );
  };

  const toggleSelection = (itemId: string) => {
    setSelectedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  };

  const deleteItem = (itemId: string) => {
    setSelectedItemId(itemId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteItem = async () => {
    if (!selectedItemId) return;
    const item = currentItems.find((i) => i.id === selectedItemId);
    try {
      if (activeSource === 'bookmarks' && item?.type === 'bookmark') {
        await api.delete(`/bookmarks/${selectedItemId}`);
        toast.success('Bookmark deleted');
      } else if (activeSource === 'bookmarks' && item?.type === 'folder') {
        toast.info('Removed folder from layout (folder kept in bookmarks)');
      } else {
        toast.success('Removed from layout');
      }
      setCurrentItems(currentItems.filter((i) => i.id !== selectedItemId));
    } catch {
      toast.error('Failed to delete item');
    } finally {
      setShowDeleteConfirm(false);
      setSelectedItemId(null);
    }
  };

  const duplicateItem = (item: DraggableItem) => {
    const newItem: DraggableItem = {
      ...item,
      id: `${item.id}-copy-${Date.now()}`,
      name: `${item.name} (Copy)`,
      children: item.children?.map((child) => ({
        ...child,
        id: `${child.id}-copy-${Date.now()}`,
      })),
    };
    setCurrentItems([...currentItems, newItem]);
    toast.success('Item duplicated');
  };

  const handleReorder = (newOrder: DraggableItem[]) => {
    setCurrentItems(newOrder);
    setIsDragging(false);
  };

  const persistDraft = useCallback(() => {
    const draft = readDraft();
    writeDraft({
      ...draft,
      tabs: tabs.map((t) => t.id),
      bookmarks: bookmarks.map((b) => b.id),
      sessions: sessions.map((s) => s.id),
      viewMode,
      activeSource,
    });
  }, [tabs, bookmarks, sessions, viewMode, activeSource]);

  const handleApplyChanges = async () => {
    setSaving(true);
    try {
      persistDraft();
      const payload = layoutPayload(activeSource, viewMode, currentItems, sessionId);
      await tabService.saveVisualLayout(
        `Drag & Drop — ${activeSource} — ${new Date().toLocaleString()}`,
        payload,
      );
      if (activeSource === 'tabs' && sessionId && currentItems.length >= 2) {
        const tabIds = currentItems.filter((i) => i.type === 'tab').map((i) => i.id);
        if (tabIds.length >= 2) {
          try {
            await tabService.createTabGroup(`Ordered ${new Date().toLocaleDateString()}`, tabIds, sessionId);
            toast.success('Tab order saved and group created');
          } catch {
            toast.success('Layout saved (group creation skipped)');
          }
        } else {
          toast.success('Layout applied');
        }
      } else {
        toast.success('Layout applied');
      }
      setDirty(false);
      await refreshLayouts();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to apply changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLayout = async (name: string) => {
    setShowSaveLayout(false);
    setSaving(true);
    try {
      persistDraft();
      await tabService.saveVisualLayout(name.trim() || 'Drag & Drop layout', {
        ...layoutPayload(activeSource, viewMode, currentItems, sessionId),
        named: true,
      });
      toast.success('Layout saved');
      setDirty(false);
      await refreshLayouts();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    const payload = {
      ...layoutPayload(activeSource, viewMode, currentItems, sessionId),
      exported_at: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `khiimori-drag-drop-${activeSource}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Layout exported');
  };

  const handleImportFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result)) as Record<string, unknown>;
        const source = data.source as SourceType | undefined;
        const items = data.items as DraggableItem[] | undefined;
        const order = data.item_order as string[] | undefined;
        if (!items?.length) {
          toast.error('Invalid layout file — no items found');
          return;
        }
        const ordered = applyOrder(items, order);
        if (source === 'tabs') setTabs(ordered);
        else if (source === 'bookmarks') setBookmarks(ordered);
        else if (source === 'sessions') setSessions(ordered);
        else setCurrentItems(ordered);
        if (source) setActiveSource(source);
        const vm = data.view_mode as ViewMode | undefined;
        if (vm) setViewMode(vm);
        setDirty(true);
        toast.success('Layout imported — click Apply Changes to persist');
      } catch {
        toast.error('Could not parse layout file');
      }
    };
    reader.readAsText(file);
  };

  const handleAddItem = async () => {
    const name = addForm.name.trim();
    const url = addForm.url.trim();
    if (!name && !url) {
      toast.error('Enter a name or URL');
      return;
    }
    try {
      if (activeSource === 'tabs') {
        if (!sessionId) {
          toast.error('No workspace session — open tabs in Khiimori first');
          return;
        }
        if (url) {
          const created = await tabService.createTab(sessionId, {
            title: name || url,
            url,
          });
          setTabs([...tabs, mapTab(created)]);
        } else {
          setTabs([
            ...tabs,
            {
              id: `tab-local-${Date.now()}`,
              name: name || 'New tab',
              type: 'tab',
              color: colorFromString(name),
              metadata: { url: '' },
            },
          ]);
        }
      } else if (activeSource === 'bookmarks') {
        if (!url) {
          toast.error('URL is required for bookmarks');
          return;
        }
        await api.post('/bookmarks', {
          title: name || url,
          url,
          description: '',
          tags: [],
        });
        const bookmarksRes = await api.get<{ bookmarks: BookmarkRow[] }>('/bookmarks');
        const foldersRes = await api.get<{ folders: BookmarkFolderRow[] }>('/bookmark-folders');
        setBookmarks(
          buildBookmarkItems(bookmarksRes.data.bookmarks ?? [], foldersRes.data.folders ?? []),
        );
      } else {
        toast.info('Sessions are managed in Settings → Sessions');
        setShowAddModal(false);
        return;
      }
      setDirty(true);
      setShowAddModal(false);
      setAddForm({ name: '', url: '' });
      toast.success('Item added');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add item');
    }
  };

  const openItem = (item: DraggableItem) => {
    const url = item.metadata?.url;
    if (url) {
      openBrowserTabs([url], true);
    }
  };

  const isTabBookmarked = useCallback(
    (item: DraggableItem) => {
      if (item.type !== 'tab') return false;
      if (bookmarkedTabIds.has(item.id)) return true;
      const url = item.metadata?.url;
      return Boolean(url && bookmarkedUrls.has(url));
    },
    [bookmarkedTabIds, bookmarkedUrls],
  );

  const refreshBookmarkIndex = useCallback(async () => {
    try {
      const bookmarksRes = await api.get<{ bookmarks: BookmarkRow[] }>('/bookmarks');
      const foldersRes = await api.get<{ folders: BookmarkFolderRow[] }>('/bookmark-folders');
      const bookmarkRows = bookmarksRes.data.bookmarks ?? [];
      const { tabIds, urls } = indexBookmarkedTabs(bookmarkRows);
      setBookmarkedTabIds(tabIds);
      setBookmarkedUrls(urls);
      setBookmarks(buildBookmarkItems(bookmarkRows, foldersRes.data.folders ?? []));
    } catch {
      // keep current index
    }
  }, []);

  const bookmarkTab = async (item: DraggableItem) => {
    if (item.type !== 'tab') return;
    if (isTabBookmarked(item)) {
      toast.info('Already bookmarked');
      return;
    }
    setBookmarkingId(item.id);
    try {
      await api.post('/bookmarks', { tab_id: item.id });
      setBookmarkedTabIds((prev) => new Set([...prev, item.id]));
      if (item.metadata?.url) {
        setBookmarkedUrls((prev) => new Set([...prev, item.metadata!.url!]));
      }
      toast.success('Tab saved to bookmarks');
      void refreshBookmarkIndex();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to bookmark tab');
    } finally {
      setBookmarkingId(null);
    }
  };

  const bookmarkSelectedTabs = async () => {
    const tabItems = currentItems.filter(
      (item) => item.type === 'tab' && selectedItems.includes(item.id) && !isTabBookmarked(item),
    );
    if (tabItems.length === 0) {
      toast.info('Selected tabs are already bookmarked');
      return;
    }
    try {
      await Promise.all(tabItems.map((item) => api.post('/bookmarks', { tab_id: item.id })));
      setBookmarkedTabIds((prev) => {
        const next = new Set(prev);
        tabItems.forEach((item) => next.add(item.id));
        return next;
      });
      setBookmarkedUrls((prev) => {
        const next = new Set(prev);
        tabItems.forEach((item) => {
          if (item.metadata?.url) next.add(item.metadata.url);
        });
        return next;
      });
      toast.success(`${tabItems.length} tab(s) saved to bookmarks`);
      setSelectedItems([]);
      void refreshBookmarkIndex();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to bookmark tabs');
    }
  };

  const restoreSavedLayout = async (layoutId: string) => {
    const entry = savedLayouts.find((l) => l.id === layoutId);
    if (!entry?.layout || typeof entry.layout !== 'object') return;
    const L = entry.layout as Record<string, unknown>;
    const source = L.source as SourceType | undefined;
    const items = L.items as DraggableItem[] | undefined;
    const order = L.item_order as string[] | undefined;
    if (!items?.length) {
      toast.error('Layout has no items');
      return;
    }
    const ordered = applyOrder(items, order);
    if (source === 'tabs') setTabs(ordered);
    else if (source === 'bookmarks') setBookmarks(ordered);
    else if (source === 'sessions') setSessions(ordered);
    if (source) setActiveSource(source);
    const vm = L.view_mode as ViewMode | undefined;
    if (vm) setViewMode(vm);
    setDirty(true);
    toast.success(`Restored "${entry.name}"`);
  };

  const renderItemActions = (item: DraggableItem) => (
    <div className="flex items-center gap-1">
      {item.type === 'tab' ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void bookmarkTab(item);
          }}
          disabled={bookmarkingId === item.id}
          className="p-1.5 hover:bg-khiimori-bg-tertiary rounded transition-colors disabled:opacity-50"
          title={isTabBookmarked(item) ? 'Bookmarked' : 'Save to bookmarks'}
        >
          <Star
            size={14}
            className={
              isTabBookmarked(item)
                ? 'text-amber-400 fill-amber-400'
                : 'text-khiimori-text-muted'
            }
          />
        </button>
      ) : null}
      {item.metadata?.url ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openItem(item);
          }}
          className="p-1.5 hover:bg-khiimori-bg-tertiary rounded transition-colors"
          title="Open in browser"
        >
          <ExternalLink size={14} className="text-khiimori-text-muted" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          duplicateItem(item);
        }}
        className="p-1.5 hover:bg-khiimori-bg-tertiary rounded transition-colors"
      >
        <Copy size={14} className="text-khiimori-text-muted" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          deleteItem(item.id);
        }}
        className="p-1.5 hover:bg-khiimori-bg-tertiary rounded transition-colors"
      >
        <Trash2 size={14} className="text-red-400" />
      </button>
    </div>
  );

  const renderItemCard = (item: DraggableItem) => (
    <div
      className={`bg-khiimori-bg-elevated rounded-xl p-4 border transition-colors cursor-pointer ${
        selectedItems.includes(item.id)
          ? 'border-purple-500 ring-1 ring-purple-500'
          : 'border-khiimori-border hover:border-khiimori-border'
      }`}
      onClick={() => toggleSelection(item.id)}
      onDoubleClick={() => openItem(item)}
    >
      <div className="flex items-start gap-3">
        <div className="cursor-grab active:cursor-grabbing text-khiimori-text-muted hover:text-khiimori-text-secondary">
          <GripVertical size={16} />
        </div>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: item.color || '#8b5cf6' }}
        >
          {item.metadata?.favicon ? (
            <img src={item.metadata.favicon} alt="" className="w-5 h-5 rounded" />
          ) : (
            <span className="text-khiimori-text-primary">{getItemIcon(item)}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-khiimori-text-primary truncate">{item.name}</h4>
          <span className="text-xs text-khiimori-text-muted capitalize">{item.type}</span>
          {item.metadata?.url ? (
            <p className="text-xs text-khiimori-text-muted truncate mt-1">{item.metadata.url}</p>
          ) : null}
          {item.type === 'session' && item.metadata?.tabCount != null ? (
            <p className="text-xs text-khiimori-text-muted mt-1">{item.metadata.tabCount} tabs</p>
          ) : null}
        </div>
        {renderItemActions(item)}
      </div>

      {item.type === 'folder' && item.children && item.children.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-khiimori-border">
          <button
            type="button"
            className="text-xs text-purple-400 mb-2"
            onClick={(e) => {
              e.stopPropagation();
              toggleFolder(item.id);
            }}
          >
            {expandedFolders.includes(item.id) ? 'Hide' : 'Show'} {item.children.length} bookmarks
          </button>
          {expandedFolders.includes(item.id) ? (
            <div className="space-y-2">
              {item.children.map((child) => (
                <div key={child.id} className="flex items-center gap-2 text-sm text-khiimori-text-muted pl-2">
                  <Bookmark size={12} />
                  <span className="truncate">{child.name}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  const renderListRow = (item: DraggableItem) => (
    <div
      className={`flex items-center gap-4 p-4 bg-khiimori-bg-elevated rounded-xl border transition-colors ${
        selectedItems.includes(item.id) ? 'border-purple-500' : 'border-khiimori-border hover:border-khiimori-border'
      }`}
      onClick={() => toggleSelection(item.id)}
      onDoubleClick={() => openItem(item)}
    >
      <GripVertical size={18} className="text-khiimori-text-muted cursor-grab shrink-0" />
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: item.color || '#8b5cf6' }}
      >
        <span className="text-khiimori-text-primary">{getItemIcon(item)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-khiimori-text-primary truncate">{item.name}</h4>
        <span className="text-xs text-khiimori-text-muted capitalize">{item.type}</span>
      </div>
      {renderItemActions(item)}
    </div>
  );

  const renderCompactRow = (item: DraggableItem) => (
    <div
      className={`flex items-center gap-2 p-2 rounded-lg bg-khiimori-bg-elevated border cursor-pointer transition-colors ${
        selectedItems.includes(item.id) ? 'border-purple-500' : 'border-khiimori-border hover:border-khiimori-border'
      }`}
      onClick={() => toggleSelection(item.id)}
    >
      <GripVertical size={14} className="text-khiimori-text-muted shrink-0" />
      <div
        className="w-6 h-6 rounded flex items-center justify-center shrink-0"
        style={{ backgroundColor: item.color || '#8b5cf6' }}
      >
        <span className="text-khiimori-text-primary text-xs">{getItemIcon(item)}</span>
      </div>
      <span className="text-sm text-khiimori-text-secondary flex-1 truncate">{item.name}</span>
    </div>
  );

  const renderReorderable = () => {
    if (!canReorder) {
      return (
        <div className="space-y-2">
          <p className="text-sm text-khiimori-text-muted mb-4">Clear search to drag and reorder items.</p>
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map((item) => (
                <div key={item.id}>{renderItemCard(item)}</div>
              ))}
            </div>
          )}
          {viewMode === 'list' && filteredItems.map((item) => <div key={item.id}>{renderListRow(item)}</div>)}
          {viewMode === 'compact' && filteredItems.map((item) => <div key={item.id}>{renderCompactRow(item)}</div>)}
        </div>
      );
    }

    const groupClass =
      viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-2';

    return (
      <Reorder.Group
        axis="y"
        values={currentItems}
        onReorder={handleReorder}
        className={groupClass}
      >
        {currentItems.map((item) => (
          <Reorder.Item
            key={item.id}
            value={item}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setIsDragging(false)}
            className={isDragging ? 'opacity-80' : ''}
          >
            {viewMode === 'grid' && renderItemCard(item)}
            {viewMode === 'list' && renderListRow(item)}
            {viewMode === 'compact' && renderCompactRow(item)}
          </Reorder.Item>
        ))}
      </Reorder.Group>
    );
  };

  return (
    <div className="h-full flex flex-col bg-khiimori-bg-secondary">
      <div className="border-b border-khiimori-border p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-khiimori-text-primary">Drag & Drop Interface</h1>
            <p className="text-sm text-khiimori-text-muted mt-1">Organize and arrange your workspace items</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => void loadData()}
              disabled={loading}
              className="px-3 py-2 bg-khiimori-bg-elevated text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <input
              ref={importRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImportFile(file);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => importRef.current?.click()}
              className="px-4 py-2 bg-khiimori-bg-elevated text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-colors flex items-center gap-2"
            >
              <Upload size={16} />
              Import
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="px-4 py-2 bg-khiimori-bg-elevated text-khiimori-text-secondary rounded-lg hover:bg-khiimori-bg-tertiary transition-colors flex items-center gap-2"
            >
              <Download size={16} />
              Export
            </button>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-400 transition-colors flex items-center gap-2"
            >
              <Plus size={16} />
              Add Item
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            {(
              [
                { id: 'tabs' as const, label: 'Tabs', icon: <Layers size={14} /> },
                { id: 'bookmarks' as const, label: 'Bookmarks', icon: <Bookmark size={14} /> },
                { id: 'sessions' as const, label: 'Sessions', icon: <Clock size={14} /> },
              ] as const
            ).map((source) => (
              <button
                key={source.id}
                type="button"
                onClick={() => {
                  setActiveSource(source.id);
                  setSelectedItems([]);
                }}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  activeSource === source.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-khiimori-bg-elevated text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary'
                }`}
              >
                {source.icon}
                {source.label}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          <div className="flex items-center gap-1 bg-khiimori-bg-elevated rounded-lg p-1">
            {(
              [
                { id: 'grid' as const, icon: <LayoutGrid size={16} /> },
                { id: 'list' as const, icon: <List size={16} /> },
                { id: 'compact' as const, icon: <Grid size={16} /> },
              ] as const
            ).map((mode) => (
              <button
                key={mode.id}
                type="button"
                onClick={() => setViewMode(mode.id)}
                className={`p-2 rounded transition-colors ${
                  viewMode === mode.id ? 'bg-primary-500 text-white' : 'text-khiimori-text-muted hover:text-white'
                }`}
              >
                {mode.icon}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 max-w-xs min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-khiimori-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items..."
              className="w-full pl-10 pr-4 py-2 bg-khiimori-bg-elevated border border-khiimori-border rounded-lg text-khiimori-text-primary placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>
          {savedLayouts.length > 0 ? (
            <select
              className="px-3 py-2 bg-khiimori-bg-elevated border border-khiimori-border rounded-lg text-sm text-khiimori-text-secondary"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) void restoreSavedLayout(e.target.value);
                e.target.value = '';
              }}
            >
              <option value="">Restore saved layout…</option>
              {savedLayouts.map((layout) => (
                <option key={layout.id} value={layout.id}>
                  {layout.name}
                </option>
              ))}
            </select>
          ) : null}
          {selectedItems.length > 0 ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-khiimori-text-muted">{selectedItems.length} selected</span>
              <button
                type="button"
                onClick={() => setSelectedItems([])}
                className="text-sm text-purple-400 hover:text-purple-300"
              >
                Clear
              </button>
              {activeSource === 'tabs' ? (
                <button
                  type="button"
                  onClick={() => void bookmarkSelectedTabs()}
                  className="px-3 py-1 bg-amber-500/20 text-amber-300 text-sm rounded-lg hover:bg-amber-500/30 flex items-center gap-1"
                >
                  <Star size={12} className="fill-amber-300" />
                  Bookmark
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  setCurrentItems(currentItems.filter((item) => !selectedItems.includes(item.id)));
                  setSelectedItems([]);
                }}
                className="px-3 py-1 bg-khiimori-bg-tertiary text-khiimori-text-secondary text-sm rounded-lg"
              >
                Remove from view
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-khiimori-text-muted">
            <Loader2 className="w-8 h-8 animate-spin mb-3" />
            <p>Loading workspace items…</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Layers size={48} className="mx-auto text-khiimori-text-muted mb-4" />
            <h3 className="text-lg font-medium text-khiimori-text-secondary">No items found</h3>
            <p className="text-khiimori-text-muted mt-2">
              {searchQuery ? 'Try a different search or clear the filter' : 'Add items or sync tabs from the extension'}
            </p>
            {!searchQuery ? (
              <Button className="mt-4" onClick={() => setShowAddModal(true)} leftIcon={<Plus size={16} />}>
                Add Item
              </Button>
            ) : null}
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            <motion.div key={`${activeSource}-${viewMode}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {renderReorderable()}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      <div className="border-t border-khiimori-border p-4 bg-khiimori-bg-secondary">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-khiimori-text-muted">
            {currentItems.length} items • Drag to reorder
            {dirty ? ' • Unsaved changes' : ''}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSaveLayout(true)}
              disabled={saving || currentItems.length === 0}
              className="px-3 py-1.5 bg-khiimori-bg-tertiary text-khiimori-text-secondary text-sm rounded-lg hover:bg-khiimori-bg-elevated transition-colors disabled:opacity-40"
            >
              <Download size={14} className="inline mr-1" />
              Save Layout
            </button>
            <button
              type="button"
              onClick={() => void handleApplyChanges()}
              disabled={saving || currentItems.length === 0}
              className="px-3 py-1.5 bg-primary-500 text-white text-sm rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-40 flex items-center gap-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              Apply Changes
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={`Add ${activeSource.slice(0, -1)}`}
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={addForm.name}
            onChange={(e) => setAddForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={activeSource === 'sessions' ? 'Session name' : 'Display name'}
          />
          {(activeSource === 'tabs' || activeSource === 'bookmarks') && (
            <Input
              label="URL"
              value={addForm.url}
              onChange={(e) => setAddForm((prev) => ({ ...prev, url: e.target.value }))}
              placeholder="https://example.com"
            />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => void handleAddItem()}>Add</Button>
          </div>
        </div>
      </Modal>

      <PromptDialog
        isOpen={showSaveLayout}
        onClose={() => setShowSaveLayout(false)}
        onSubmit={(name) => void handleSaveLayout(name)}
        title="Save layout"
        description="Name this arrangement so you can restore it later."
        defaultValue={`Drag & Drop — ${activeSource} — ${new Date().toLocaleDateString()}`}
        submitText="Save"
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => void confirmDeleteItem()}
        title="Delete Item"
        message={
          activeSource === 'bookmarks'
            ? 'Delete this bookmark permanently?'
            : 'Remove this item from the layout?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};
